import { useParams } from 'react-router-dom'
import { useGlobalErrorHandler, useMutation } from '@src/hooks'
import { CarService, ReportService, UserService } from '@src/domain/services'
import { Category, GetCarInfoRespBo, GetPIOItemArgsRespBo } from '@src/domain/services/report.services'
import { useDispatch, useSelector, reportActions } from '@src/store'
import { useEffect, useState } from 'react'
import { useSpinner } from '@src/components/spinner'
import { CarRecordDetailModal } from './components/CarRecordDetailModal'
import { ModalResolveType, useModal } from '@src/lib/modal-manager'
import { EditPioItemArgsModal } from './components/EditPioItemArgs'
import { useDialog } from '@src/components/dialog'
import { EditCarInfoModal } from './components/EditCarInfo'

export interface SearchCondition { [name: string]: string }

const searchConditionInitDto: SearchCondition = {
  factoryUuid: '',
  status: '',
  carModelUuid: '',
  engineNo: '',
  sfxNo: '',
  shippingNo: '',
  category: 'assembly',
  fromDate: '',
  fromTime: '',
  toDate: '',
  toTime: '',
  stationUuid: '',
  name: '',
  employeeNo: '',
  employee: '',
  pioItemUuid: '',
  idotNo: '',
  keyword: '',
  page: '',
  pageSize: ''
}

export function ReportViewModel() {
  const params = useParams()
  const category = params.category as Category
  const [currentPage, setPage] = useState(1)
  const [factoryOptions, setFactoryOptions] = useState<UserService.FactoriesBo>([])
  const [carModelOptions, setCarModelOptions] = useState<CarService.CarModelsBo>([])
  const [pioItemOptions, setPioItemOptions] = useState<CarService.PioItemsBo>([])
  const [searchCondition, setSearchCondition] = useState<SearchCondition>(searchConditionInitDto)
  const dispatch = useDispatch()
  const { spinner } = useSpinner()
  const { showModal } = useModal()
  const { dialog } = useDialog()
  const { globalErrorHandler } = useGlobalErrorHandler()

  const { mutate: getCarRecords } = useMutation(ReportService.getCarRecords)
  const { mutate: getCarRecordDetail } = useMutation(ReportService.getCarRecordDetail)
  const { mutate: getCarInfos } = useMutation(ReportService.getCarInfos)
  const { mutate: getCarInfo } = useMutation(ReportService.getCarInfo)
  const { mutate: updateCarInfo } = useMutation(ReportService.updateCarInfo)
  const { mutate: deleteCarInfo } = useMutation(ReportService.deleteCarInfo)
  const { mutate: getPIORecords } = useMutation(ReportService.getPIORecords)
  const { mutate: getPIOItemArgs } = useMutation(ReportService.getPIOItemArgs)
  const { mutate: updatePIOItemArgs } = useMutation(ReportService.updatePIOItemArgs)
  const { mutate: getEmployeeRecords } = useMutation(ReportService.getEmployeeRecords)
  const { mutate: getCarStatistics } = useMutation(ReportService.getCarStatistics)
  const { mutate: getTotalOutput } = useMutation(ReportService.getTotalOutput)
  const { mutate: exportCarRecords } = useMutation(ReportService.exportCarRecords)
  const { mutate: exportCarInfos } = useMutation(ReportService.exportCarInfos)
  const { mutate: exportPIORecords } = useMutation(ReportService.exportPIORecords)
  const { mutate: exportEmployeeRecords } = useMutation(ReportService.exportEmployeeRecords)
  const { mutate: exportCarStatistics } = useMutation(ReportService.exportCarStatistics)
  const { mutate: exportTotalOutput } = useMutation(ReportService.exportTotalOutput)
  const { mutate: getFactories } = useMutation(UserService.getFactories)
  const { mutate: getCarModels } = useMutation(CarService.getCarModels)
  const { mutate: getPioItems } = useMutation(CarService.getPioItems)

  const { itemListData } = useSelector(state => state.report)

  const queryFunctionMap = {
    'car-records': getCarRecords,
    'car-infos': getCarInfos,
    'pio-records': getPIORecords,
    'employee-records': getEmployeeRecords,
    'car-statistics': getCarStatistics,
    'total-output': getTotalOutput
  }

  const exportFunctionMap = {
    'car-records': exportCarRecords,
    'car-infos': exportCarInfos,
    'pio-records': exportPIORecords,
    'employee-records': exportEmployeeRecords,
    'car-statistics': exportCarStatistics,
    'total-output': exportTotalOutput,
  }
  
  async function handleCategoryQuery(searchCondition: SearchCondition): Promise<any> {
    const selectedFunction = queryFunctionMap[category]
    if (selectedFunction) {
      const result = await spinner(selectedFunction({searchCondition}))
      return result
    } else {
      console.error('Unknown category:', category);
    }
  }

  async function handleCategoryDownload(searchCondition: SearchCondition): Promise<void> {
    const selectedFunction = exportFunctionMap[category]
    if (selectedFunction) {
      await spinner(selectedFunction({searchCondition}))
    } else {
      console.error('Unknown category:', category);
    }
  }

  const getFactoryOptions = async () => {
    const options = await spinner(getFactories())
    setFactoryOptions(options)
  }

  const getCarModelOptions = async () => {
    const options = await spinner(getCarModels())
    setCarModelOptions(options)
  }

  const getPioItemOptions = async () => {
    const options = await spinner(getPioItems())
    setPioItemOptions(options)
  }

  useEffect(() => {
    getFactoryOptions()
    getCarModelOptions()
    getPioItemOptions()
    setSearchCondition(searchConditionInitDto)
    dispatch(
      reportActions.setItemList(null)
    )
    return () => {
      setPage(1)
    }
  }, [category])

  const getItemListByPageNumber = async (pageNumber: number, searchCondition: SearchCondition) => {
    const itemListDto = await handleCategoryQuery({ ...searchCondition, page: `${pageNumber}` })

    dispatch(
      reportActions.setItemList(itemListDto)
    )
  }

  const searchOnClick = async (searchCondition: SearchCondition) => {
    getItemListByPageNumber(1, searchCondition)
    setPage(1)
    setSearchCondition(searchCondition)
  }

  const downloadOnClick = async (searchCondition: SearchCondition) => {
    handleCategoryDownload(searchCondition)
  }

  const handleNextOnClick = async () => {
    if (currentPage === itemListData?.totalPages) return
    await getItemListByPageNumber(currentPage + 1, searchCondition)
    setPage(currentPage + 1)
  }
  
  const handlePrevOnClick = async () => {
    if (currentPage === 1) return
    await getItemListByPageNumber(currentPage - 1, searchCondition)
    setPage(currentPage - 1)
  }

  const handleMoveToOnClick = async (pageNumber: number) => {
    await getItemListByPageNumber(pageNumber, searchCondition)
    setPage(pageNumber)
  }

  const handleDetailOnClick = async (rowData: any) => {
    try {
      const { engineNo, stationUuid } = rowData
      const itemDto = await spinner(getCarRecordDetail({ engineNo, stationUuid, searchCondition }))

      await showModal(CarRecordDetailModal, { itemDto, category: searchCondition.category })

    } catch (error) {
      globalErrorHandler(error)
    }
  }

  const handleEditPioBtnOnClick = async (rowData: any) => {
    try {
      const { engineNo } = rowData
      const { pioItemUuid } = searchCondition
      const itemDto = await spinner(getPIOItemArgs({ engineNo, pioItemUuid }))

      const result = await showModal(EditPioItemArgsModal, { itemDto })

      if(result.type !== ModalResolveType.OK) return

      const data = result.data as GetPIOItemArgsRespBo

      await spinner(updatePIOItemArgs({ engineNo, pioItemUuid, data }))

      await searchOnClick(searchCondition)
    } catch (error) {
      globalErrorHandler(error)
    }
  }

  const handleEditCarInfo = async (rowData: any) => {
    try {
      const { engineNo } = rowData
      const itemDto = await spinner(getCarInfo({ engineNo }))
      
      const result = await showModal(EditCarInfoModal, { itemDto })

      if(result.type !== ModalResolveType.OK) return

      const data = result.data as GetCarInfoRespBo

      await spinner(updateCarInfo({ engineNo, data }))

      await searchOnClick(searchCondition)
    } catch (error) {
      globalErrorHandler(error)
    }
  }

  const handleDeleteCarInfo = async (rowData: any) => {
    const { engineNo } = rowData
    const result = await dialog.confirm({
      title: '刪除確認',
      message: '是否要刪除車輛資料'
    })

    if (result.type !== ModalResolveType.OK) return

    try {
      await spinner(deleteCarInfo({ engineNo }))
      await searchOnClick(searchCondition)
    } catch (error) {
      globalErrorHandler(error)
    }
  }

  return {
    category,
    currentPage,
    factoryOptions,
    carModelOptions,
    pioItemOptions,
    itemListData,
    searchCondition,
    searchOnClick,
    downloadOnClick,
    handleNextOnClick,
    handlePrevOnClick,
    handleMoveToOnClick,
    handleDetailOnClick,
    handleEditPioBtnOnClick,
    handleEditCarInfo,
    handleDeleteCarInfo
  }
}

export type IReportViewModel = ReturnType<typeof ReportViewModel>
